/**
 * Link URL sanitization for the rich-text editor.
 *
 * SECURITY: editor content is rendered elsewhere via `dangerouslySetInnerHTML`
 * (e.g. the email-template preview) with no HTML sanitizer in that path, so an
 * `href` like `javascript:` / `data:` would be a stored-XSS vector. Every URL a
 * user supplies for a link MUST pass through {@link sanitizeLinkHref} before it
 * reaches the document.
 */

/**
 * Schemes we allow as absolute link targets. Anything else with a scheme
 * (`javascript:`, `data:`, `vbscript:`, `file:`, …) is rejected.
 */
const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/** Matches a leading URI scheme, e.g. `https:` or `javascript:`. */
const SCHEME_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

/**
 * Normalize and validate a user-supplied link URL.
 *
 * Returns a safe href string, or `null` when the input is empty or uses a
 * disallowed scheme (caller should surface an error and not apply the link).
 *
 * Rules (fail-safe — unknown shapes default to an `https://` prefix rather than
 * being trusted verbatim):
 * - empty / whitespace-only → `null`
 * - relative (`/path`, `#anchor`, `?q=`) → returned unchanged
 * - protocol-relative (`//host/…`) → upgraded to `https://host/…`
 * - explicit scheme → allowed only if in {@link ALLOWED_PROTOCOLS}
 * - bare host (`example.com`) → prefixed with `https://`
 */
export function sanitizeLinkHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Relative URLs carry no scheme and cannot introduce a dangerous protocol.
  if (/^[/#?]/.test(trimmed)) {
    // A protocol-relative URL (`//host`) is upgraded to https so it is never
    // interpreted against a `javascript:`-like base.
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    return trimmed;
  }

  const schemeMatch = SCHEME_RE.exec(trimmed);
  if (schemeMatch) {
    const protocol = `${schemeMatch[1].toLowerCase()}:`;
    return ALLOWED_PROTOCOLS.has(protocol) ? trimmed : null;
  }

  // No scheme and not obviously relative → treat as a bare host.
  return `https://${trimmed}`;
}
